import { createContext, useContext, useReducer, useRef, useState } from 'react';
import LangEnum from '../../lang/LangEnum';
import { Country } from '../../lib/countries';
import { unmaskCpfCnpj } from '../../utils/validators';
import { storeRepository } from '../store/store.repository';
import { useTranslation } from '../translation/TranslationContext';
import { cnpjSchema } from './cnpj.schema';
import {
  changeStep,
  completeStoreStep,
  fetchCnpjAlreadyExistsError,
  fetchCnpjError,
  fetchCnpjSuccess,
  resetCnpjStatus,
  setCnpj,
  setLocation,
  setUserMode,
  signUpInitialState,
  signUpReducer,
  startFetchCnpj,
} from './signUp.state';
import { CreateAccountStore } from './types/createAccount.types';
import { SignUpState, SignUpSteps, SignUpUserMode } from './types/signUp.types';

type SignUpContextProviderProps = {
  children: React.ReactNode;
};

type SignUpContextProps = {
  state: SignUpState;
  handleChangeLocation: (location: Country) => void;
  handleChangeCnpj: (cnpj: string) => void;
  handleResetCnpjStatus: () => void;
  handleChangeStep: (step: SignUpSteps) => void;
  handleCompleteStoreStep: (formData: CreateAccountStore) => void;
  handleSetUserMode: (mode: SignUpUserMode) => void;
  validateCnpj: (value: string) => Promise<boolean>;
  registerStepSubmit: (fn: (() => void | Promise<void>) | null) => void;
  submitCurrentStep: () => void | Promise<void>;
  isStepSubmitting: boolean;
  setStepSubmitting: (value: boolean) => void;
};

export const SignUpContext = createContext<SignUpContextProps>({
  state: signUpInitialState,
  handleChangeLocation: () => {
    return;
  },
  handleChangeCnpj: () => {
    return;
  },
  handleResetCnpjStatus: () => {
    return;
  },
  handleChangeStep: () => {
    return;
  },
  validateCnpj: async () => false,
  handleCompleteStoreStep: () => {
    return;
  },
  handleSetUserMode: () => {
    return;
  },
  registerStepSubmit: () => {
    return;
  },
  submitCurrentStep: () => {
    return;
  },
  isStepSubmitting: false,
  setStepSubmitting: () => {
    return;
  },
});

export const SignUpContextProvider = ({ children }: SignUpContextProviderProps) => {
  const [state, dispatch] = useReducer(signUpReducer, signUpInitialState);
  const { translate } = useTranslation();
  const stepSubmitRef = useRef<(() => void | Promise<void>) | null>(null);
  const [isStepSubmitting, setStepSubmitting] = useState(false);

  const registerStepSubmit = (fn: (() => void | Promise<void>) | null) => {
    stepSubmitRef.current = fn;
  };

  const submitCurrentStep = () => stepSubmitRef.current?.();

  const handleChangeCnpj = (cnpj: string) => dispatch(setCnpj(cnpj));

  const handleResetCnpjStatus = () => dispatch(resetCnpjStatus());

  const handleChangeLocation = (location: Country) => {
    if (location?.code !== 'BR') {
      dispatch(setCnpj(''));
      dispatch(resetCnpjStatus());
    }
    dispatch(setLocation(location));
  };

  const handleChangeStep = (step: SignUpSteps) => dispatch(changeStep(step));

  const handleCompleteStoreStep = (formData: CreateAccountStore) => {
    dispatch(completeStoreStep(formData));
    handleChangeStep(SignUpSteps.USER);
  };

  const handleSetUserMode = (mode: SignUpUserMode) => dispatch(setUserMode(mode));

  const validateCnpj = async (value: string): Promise<boolean> => {
    if (value === '') {
      dispatch(resetCnpjStatus());
      return false;
    }

    if (!cnpjSchema.safeParse(value).success) {
      dispatch(fetchCnpjError(translate(LangEnum.GLOBAL_INVALID_CNPJ)));
      return false;
    }

    dispatch(startFetchCnpj());

    const repository = storeRepository();

    const response = await repository.validateCnpjForSignUp(unmaskCpfCnpj(value));

    if (!response.ok && response.data) {
      const error = response.data.message;
      const isCnpjAlreadyExistsError =
        response.data.code === LangEnum.API_EXCEPTION_STORE_ALREADY_REGISTERED;
      dispatch(
        isCnpjAlreadyExistsError
          ? fetchCnpjAlreadyExistsError(error || '')
          : fetchCnpjError(error || ''),
      );
      return false;
    }

    if (response.ok) {
      dispatch(fetchCnpjSuccess(response?.data, response?.data ? undefined : value));
      return true;
    }

    dispatch(resetCnpjStatus());
    return false;
  };

  const value = {
    state,
    dispatch,
    handleChangeLocation,
    validateCnpj,
    handleChangeCnpj,
    handleResetCnpjStatus,
    handleChangeStep,
    handleCompleteStoreStep,
    handleSetUserMode,
    registerStepSubmit,
    submitCurrentStep,
    isStepSubmitting,
    setStepSubmitting,
  };

  return <SignUpContext.Provider value={value}>{children}</SignUpContext.Provider>;
};

export const useSignUpContext = () => useContext(SignUpContext);
